import { dispatchEvent } from '$utilities/events';
import { loadScript } from '$utilities/requests';

/* ========================================================================= */
/* Shared variables */
/* ========================================================================= */

const sharedMemory = {
    isRequested: false,
    isLoaded: false,
};

/* ========================================================================= */
/* Player interface */
/* ========================================================================= */

/**
 * @class VimeoPlayer
 *
 * Vimeo player adapter used to align all the players to the same interface.
 */
class VimeoPlayer {
    /**
     * @method constructor
     * @param {HTMLElement} playerElement - The iframe element.
     * @param {object} config (optional) - The Vimeo player config.
     *
     * 1. Loads the resources if they are not requested yet.
     * 2. If the resources are already available, initialize the original player.
     * 3. Otherwise if the resources are not loaded yet, wait for it.
     */
    constructor (playerElement, config = {}) {
        if (!sharedMemory.isRequested) { /* [1] */
            this.loadResources();
        }

        if (sharedMemory.isLoaded) { /* [2] */
            this.init(playerElement, config);
        } else { /* [3] */
            window.addEventListener('vimeoReady', () => this.init(playerElement, config));
        }
    }

    /**
     * @method loadResources
     *
     * Loads the resources necessary to initialize the Vimeo player.
     * 1. Once the resources are loaded, communicates the information
     *    to any other stalled player.
     */
    loadResources () {
        sharedMemory.isRequested = true;

        loadScript('https://player.vimeo.com/api/player.js', () => {
            sharedMemory.isLoaded = true;
            dispatchEvent('vimeoReady'); /* [1] */
        });
    }

    /**
     * @method init
     * @param {HTMLElement} playerElement - The iframe element.
     * @param {object} config (optional) - The Vimeo player config.
     *
     * Initializes the original player.
     */
    init (playerElement, config) {
        this.originalPlayer = new window.Vimeo.Player(playerElement, config);
        this.originalPlayer.on('ended', () => this?.onEnd());

        if (this.onReady) {
            this.originalPlayer.ready().then(() => this.onReady());
        }
    }

    /**
     * @method play
     *
     * Dispatches the play method on the original player.
     */
    play () {
        if (sharedMemory.isLoaded) {
            this.originalPlayer.play();
        } else {
            this.onReady = () => this.play();
        }
    };

    /**
     * @method playAt
     *
     * Dispatches the play method on the original player at a specified time.
     */
    playAt (time) {
        if (sharedMemory.isLoaded) {
            this.originalPlayer.play();
            this.originalPlayer.setCurrentTime(time);
        } else {
            this.onReady = () => this.playAt(time);
        }
    };

    /**
     * @method pause
     *
     * Dispatches the pause method on the original player.
     */
    pause () {
        this.originalPlayer.pause();
    };
}

/* ========================================================================= */
/* Export(s) */
/* ========================================================================= */

export default VimeoPlayer;
